import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { FaBrain, FaCloud, FaCode, FaDatabase, FaDownload, FaEnvelope, FaExternalLinkAlt, FaFacebook, FaFilePdf, FaGithub, FaGlobeAfrica, FaLeaf, FaLinkedin, FaRocket, FaServer, FaTerminal, FaTools, FaUsers, FaWhatsapp } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import logo from '../assets/logo.png';
import portrait from '../assets/portfolio_avatar.png';

const LandingPage = () => {
    const [activeSection, setActiveSection] = useState('home');
    const [terminalInput, setTerminalInput] = useState('');
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [terminalHistory, setTerminalHistory] = useState([
        { type: 'bot', content: "Hello! I'm Ramspheld's virtual assistant. I can answer questions about his projects, professional experience, tech stack, or how to collaborate. What would you like to know?" }
    ]);
    const terminalEndRef = useRef(null);

    const experiences = [
        {
            role: "Founder",
            company: "Langora LLC",
            type: "Full-time",
            location: "Nairobi, Kenya",
            period: "June 2025 – Present",
            achievements: [
                "Leading the development of Langora, a trusted platform for serious English learners.",
                "Driving the product vision from zero to confident communication through structured programs."
            ]
        },
        {
            role: "Research Strategist – African AI & Data Governance",
            company: "Internet Society (ISOC) Africa",
            type: "Contract",
            location: "Nairobi, Kenya",
            period: "Jan 2025 – November 2025",
            achievements: [
                "Led research on AU Data Policy Framework supporting policy adoption in Kenya, Uganda and Zambia.",
                "Secured USD 100,000+ in funding for AI governance programs and innovation centres.",
                "Coordinated engagements with 200+ policymakers, NGOs, and pan-African institutions."
            ]
        },
        {
            role: "Python Engineer & Impact Lead Evaluator",
            company: "AI For Good – Nairobi Hub",
            type: "Full-time",
            location: "Nairobi",
            period: "Jan 2023 – September 2025",
            achievements: [
                "Developed REST APIs, dashboards, and ML systems serving 10,000+ users.",
                "Created Django and FastAPI systems integrated with logistics, finance, and notifications.",
                "Built accessibility-focused AI tools using NLP and computer vision."
            ]
        },
        {
            role: "Django Developer",
            company: "Jamii Tech Solutions",
            type: "Contract",
            location: "Remote",
            period: "November 2023 – Dec 2024",
            achievements: [
                "Built secure, scalable Django microservices for Banking operations.",
                "Integrated external APIs improving transaction reliability and system communication."
            ]
        },
        {
            role: "Software Development Tutor",
            company: "Nairobi Technical Training Institute Kenya",
            type: "Full-time",
            location: "Nairobi",
            period: "Sep 2023 – Present",
            achievements: [
                "Delivered advanced Python programming tutorials, including machine learning and data analysis.",
                "Mentored over 200 students, ensuring practical application of skills through project-based learning."
            ]
        },
        {
            role: "Software Development Tutor",
            company: "Teach Learn Africa Kenya",
            type: "Contract",
            location: "Remote",
            period: "Jan 2023 – Aug 2023",
            achievements: [
                "Facilitated workshops on full-stack web development, covering both frontend and backend technologies.",
                "Coached learners in developing impactful projects, including fintech and AI-powered tools."
            ]
        },
        {
            role: "Software Development Tutor",
            company: "ALX Foundation Program",
            type: "Contract",
            location: "Kenya",
            period: "Jan 2022 – Dec 2022",
            achievements: [
                "Delivered training on Python programming and API integration, focusing on hands-on, practical learning.",
                "Designed real-world coding challenges to improve learner engagement."
            ]
        }
    ];

    const handleTerminalSubmit = (e) => {
        e.preventDefault();
        try {
            if (!terminalInput.trim()) return;

            const input = terminalInput.toLowerCase();
            const newHistory = [...terminalHistory, { type: 'user', content: terminalInput }];
            setTerminalHistory(newHistory);
            setTerminalInput('');
            setIsBotTyping(true);

            // Context-Aware Logic
            setTimeout(() => {
                let response = "I'm not sure about that. Try asking about my projects, skills, experience, or use 'help' for a list of commands.";

                // Internal logic...
                if (input.match(/^(hello|hi|hey|greetings|yo)/)) {
                    response = [
                        "Hello! I am Ramspheld's Ethical AI Agent.",
                        "Available topics: [projects], [experience], [skills], [contact].",
                        "Type 'help' for more commands."
                    ];
                } else if (input === 'help' || input === 'ls' || input === 'dir') {
                    response = [
                        "COMMAND CENTER HELP:",
                        "▹ projects      - View key case studies",
                        "▹ experience    - Professional timeline",
                        "▹ skills        - Technical capabilities",
                        "▹ contact       - Mobile, WhatsApp, Email",
                        "▹ education     - Academic background",
                        "▹ resume        - Download Portfolio PDF"
                    ];
                } else if (input.includes('project') || input.includes('work') || input.includes('portfolio') || input.includes('contribution')) {
                    response = [
                        "KEY CONTRIBUTIONS:",
                        "▹ Langora        - Strategic English Learning Platform",
                        "▹ Monk AI        - Multi-agent LLM Systems",
                        "▹ WaveTracker    - Signal ML Analytics",
                        "▹ CBC Chatbot    - Educational AI Support",
                        "Type the project name for more details."
                    ];
                } else if (input.includes('langora')) {
                    response = [
                        "PROJECT: LANGORA",
                        "URL: https://laangora.pro/",
                        "Role: Founder & Lead Developer",
                        "Focus: Transforming language learners into confident communicators."
                    ];
                } else if (input.includes('experience') || input.includes('job') || input.includes('history') || input.includes('career')) {
                    response = [
                        "PROFESSIONAL TIMELINE:",
                        "▹ Founder        @ Langora LLC",
                        "▹ AI Research    @ Internet Society (ISOC)",
                        "▹ Python Engr    @ AI For Good",
                        "▹ Software Tutor @ Nairobi NTTI"
                    ];
                } else if (input.includes('skill') || input.includes('stack') || input.includes('tech') || input.includes('language') || input.includes('tools')) {
                    response = [
                        "CORE CAPABILITIES:",
                        "▹ Languages: Python, JavaScript, PHP",
                        "▹ Frameworks: Django, FastAPI, React, Next.js",
                        "▹ AI/ML: TensorFlow, Scikit-learn, LLMs",
                        "▹ Cloud: AWS, Google Cloud, Docker"
                    ];
                } else if (input.includes('contact') || input.includes('email') || input.includes('hire') || input.includes('reach') || input.includes('number') || input.includes('phone') || input.includes('whatsapp') || input.includes('call')) {
                    response = [
                        "HANDSHAKE PROTOCOLS:",
                        "▹ WhatsApp: +254 743 864 874",
                        "▹ Mobile:   +254 743 864 874",
                        "▹ Email:    ramspheldonyango@gmail.com",
                        "▹ LinkedIn: linkedin.com/in/ramspheld"
                    ];
                } else if (input.includes('vision') || input.includes('about') || input.includes('goal')) {
                    response = "Bridging the gap between advanced AI research and sustainable implementation in emerging markets.";
                }

                setTerminalHistory(prev => [...prev, { type: 'bot', content: response }]);
                setIsBotTyping(false);
            }, 600);
        } catch (err) {
            console.error("Terminal Error:", err);
        }
    };

    const terminalBodyRef = useRef(null);

    useEffect(() => {
        if (terminalBodyRef.current) {
            terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
        }
    }, [terminalHistory]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-20% 0px -20% 0px',
            threshold: 0.1,
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        }, observerOptions);

        const sections = ['home', 'about', 'projects', 'blogs', 'skills', 'connect'];
        sections.forEach((id) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    const coreValues = [
        { title: "Empowerment", desc: "Training the next generation of African devs.", icon: <FaUsers /> },
        { title: "Scalability", desc: "Building systems that grow with the continent.", icon: <FaRocket /> },
        { title: "Ethics", desc: "Ensuring AI serves humanity transparently.", icon: <FaBrain /> }
    ];

    const projects = [
        {
            title: 'MentorCruise',
            role: 'Mentor & Contributor',
            desc: 'Helping people break into tech and level up their skills through high-impact, 1-on-1 mentorship.',
            stack: ['Mentorship', 'Career Growth', 'Python', 'Web Dev'],
            github: '#',
            link: 'https://mentorcruise.com/'
        },
        {
            title: 'Monk AI',
            role: 'AI Engineer',
            desc: 'Building a multi-agent LLM system to automate research tasks and technical documentation workflows.',
            stack: ['LLMs', 'Multi-Agent', 'Python', 'FastAPI'],
            github: '#',
            link: 'https://monk.ai/'
        },
        {
            title: 'WaveTracker',
            role: 'Lead Developer',
            desc: 'A signal analytics platform that uses machine learning to identify and track complex wave patterns in real-time data.',
            stack: ['Data Science', 'Signal Processing', 'React', 'AWS'],
            github: '#',
            link: 'https://wavetracker.io/'
        },
        {
            title: 'Tana.inc',
            role: 'Contributor / User',
            desc: 'AI-native knowledge-management \u0026 productivity workspace. Supports voice memos, live-transcription, AI-powered summarization, and "living knowledge".',
            stack: ['AI', 'Knowledge Management', 'Productivity'],
            github: '#',
            link: 'https://tana.inc/'
        },
        {
            title: 'Langora',
            role: 'Developer',
            desc: 'English fluency built on trust. A guided platform for serious learners, offering structured programs and human expertise for confident communication.',
            stack: ['Platform Dev', 'Education'],
            github: 'https://github.com/Felix-kerich/Langora',
            link: 'https://laangora.pro/'
        },
        {
            title: 'CBC Curriculum Chatbot',
            role: 'AI Lead',
            desc: 'An AI-powered assistant designed to support the Competency-Based Curriculum (CBC) implementation and inquiries.',
            stack: ['AI', 'Chatbot', 'NLP'],
            github: 'https://github.com/RamspheldOnyangoOchieng/cbc-curriculum-chatbot',
            link: 'https://cbc-curriculum-chatbot.vercel.app/'
        }
    ];

    const blogs = [
        {
            title: "The Future of AI Governance in Africa",
            excerpt: "Exploring the critical need for locally-driven AI data policy frameworks in emerging markets.",
            date: "Jan 15, 2026",
            readTime: "6 min",
            tag: "AI Research",
            link: "#",
        },
        {
            title: "Scaling Tech Talent: My Journey as a Mentor",
            excerpt: "Reflecting on guiding 200+ developers from Nairobi Technical to global engineering roles.",
            date: "Dec 20, 2025",
            readTime: "4 min",
            tag: "Mentorship",
            link: "#",
        },
        {
            title: "Next.js & Django: The Architecture for 2026",
            excerpt: "Why this full-stack combination is winning the race for scalable, AI-powered web ecosystems.",
            date: "Nov 12, 2025",
            readTime: "8 min",
            tag: "Architecture",
            link: "#",
        }
    ];

    const shareBlog = (platform, title, link) => {
        const url = encodeURIComponent(link === '#' ? window.location.href : link);
        const text = encodeURIComponent(`Check out this insights by Ramspheld: ${title}`);

        const shares = {
            twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
            whatsapp: `https://wa.me/?text=${text}%20${url}`
        };

        window.open(shares[platform], '_blank');
    };

    const skills = [
        { name: 'Backend Development', icon: <FaServer />, list: ['Django', 'Flask', 'Node.js', 'RESTful APIs', 'Next.js', 'Laravel PHP'] },
        { name: 'Frontend Development', icon: <FaCode />, list: ['ReactJS', 'HTML5', 'CSS3', 'Bootstrap', 'AJAX'] },
        { name: 'Database Management', icon: <FaDatabase />, list: ['PostgreSQL', 'MySQL', 'MongoDB'] },
        { name: 'AI / ML Tools', icon: <FaBrain />, list: ['TensorFlow', 'Scikit-learn', 'NLTK'] },
        { name: 'DevOps', icon: <FaRocket />, list: ['CI/CD Pipelines', 'Docker', 'Kubernetes', 'Jenkins'] },
        { name: 'Cloud Technologies', icon: <FaCloud />, list: ['AWS (EC2, S3, Lambda)', 'Google Cloud'] },
        { name: 'Testing \u0026 Optimization', icon: <FaTools />, list: ['Unit testing', 'TDD', 'Debugging tools'] },
        { name: 'Soft Skills', icon: <FaUsers />, list: ['Communication', 'Mentorship', 'Adaptability', 'Problem-solving'] }
    ];

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    const socials = [
        { icon: <FaWhatsapp />, url: 'https://wa.me/254743864874', name: 'WhatsApp' },
        { icon: <FaLinkedin />, url: 'https://linkedin.com/in/ramspheld', name: 'LinkedIn' },
        { icon: <FaXTwitter />, url: 'https://x.com/RamspheldO', name: 'X' },
        { icon: <FaBrain />, url: 'https://huggingface.co/ramspheldonyango', name: 'HuggingFace' },
        { icon: <FaGithub />, url: 'https://github.com/RamspheldOnyangoOchieng', name: 'GitHub' },
        { icon: <FaFacebook />, url: 'https://facebook.com/onyangoramspheld', name: 'Facebook' },
    ];

    return (
        <div className="relative min-h-screen text-white">
            {/* Floating Navigation Dots */}
            <nav className="fixed right-6 md:right-10 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-6">
                {['home', 'about', 'projects', 'blogs', 'skills', 'connect'].map((item) => (
                    <button
                        key={item}
                        onClick={() => scrollTo(item)}
                        className="group relative flex items-center justify-end"
                    >
                        <span className={`mr-4 text-xs font-bold tracking-widest uppercase transition-all duration-300 opacity-0 group-hover:opacity-100 ${activeSection === item ? 'text-primary' : 'text-gray-500'}`}>
                            {item}
                        </span>
                        <div className={`w-2.5 h-2.5 rounded-full border-2 transition-all duration-300 ${activeSection === item ? 'bg-primary border-primary scale-150 shadow-[0_0_15px_#0ea5e9]' : 'bg-transparent border-gray-600'}`} />
                    </button>
                ))}
            </nav>

            {/* Hero Section */}
            <section id="home" className="min-h-screen flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-32 px-6 md:px-20 pt-32 pb-24 relative overflow-hidden">
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-left z-10 w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left lg:-translate-x-12"
                >
                    <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                        <div className="glass-bubble w-32 h-32 animate-float overflow-hidden rounded-full border-2 border-primary/50 shadow-[0_0_30px_rgba(14,165,233,0.3)] shrink-0">
                            <img src={portrait} alt="Ramspheld Portrait" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex flex-wrap gap-6 items-center justify-center">
                            {socials.map((social, i) => (
                                <motion.a
                                    key={social.name}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        opacity: { duration: 0.5, delay: 0.5 + (i * 0.1) },
                                        y: { duration: 0.5, delay: 0.5 + (i * 0.1) }
                                    }}
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="text-3xl text-gray-400 hover:text-primary transition-all cursor-pointer social-glow-pulse"
                                    style={{ animationDelay: `${i * 0.3}s` }}
                                    title={social.name}
                                >
                                    {social.icon}
                                </motion.a>
                            ))}
                        </div>
                    </div>
                    <h1 className="text-2xl md:text-5xl lg:text-5xl font-black mb-6 tracking-tighter leading-tight text-white drop-shadow-lg uppercase whitespace-nowrap">
                        Ramspheld Onyango
                    </h1>
                    <p className="text-base md:text-xl font-light text-gray-300 max-w-xl mb-4 leading-relaxed bg-black/30 lg:bg-transparent p-4 lg:p-0 rounded-2xl backdrop-blur-sm lg:backdrop-blur-none lowercase">
                        Full Stack Developer | Python Engineer | Mentor & Trainer
                    </p>
                    <p className="text-base md:text-xl font-light text-gray-400 max-w-xl mb-10 leading-relaxed bg-black/30 lg:bg-transparent p-4 lg:p-0 rounded-2xl backdrop-blur-sm lg:backdrop-blur-none">
                        Pioneering <span className="text-white font-semibold">Ethical Artificial Intelligence</span> &
                        <span className="text-white font-semibold"> Sustainable Technology</span> for the African continent and beyond.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                        <button onClick={() => scrollTo('projects')} className="px-8 py-4 bg-primary rounded-full font-bold hover:scale-105 transition-all shadow-lg shadow-primary/30 text-white">Explore Impact</button>
                        <a href="/Ramspheld_Resume.pdf" target="_blank" rel="noopener noreferrer" className="px-8 py-4 glass-card rounded-full font-bold hover:bg-white/10 transition-all border-white/20 flex items-center gap-2 text-white">
                            <FaFilePdf /> Portfolio PDF
                        </a>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="z-10 w-full lg:w-5/12 max-w-xl mt-10 lg:mt-0 lg:translate-x-12"
                >
                    <div className="mb-4 flex items-center gap-3">
                        <div className="h-px bg-primary/30 flex-grow" />
                        <span className="text-primary font-mono font-bold text-xs tracking-[0.2em] uppercase whitespace-nowrap">Rams' Bot Intelligent assistant</span>
                        <div className="h-px bg-primary/30 flex-grow" />
                    </div>
                    <div className="terminal-window rounded-2xl shadow-2xl border border-white/10 bg-[#020617]/90 backdrop-blur-xl">
                        <div className="terminal-header items-center">
                            <div className="flex gap-2">
                                <div className="terminal-dot bg-red-500" />
                                <div className="terminal-dot bg-yellow-500" />
                                <div className="terminal-dot bg-green-500" />
                            </div>
                            <span className="ml-auto text-[10px] text-primary/80 font-mono tracking-widest uppercase flex items-center gap-2">
                                <FaTerminal className="text-[10px]" /> ETHICAL_AI_SHELL
                            </span>
                        </div>
                        <div ref={terminalBodyRef} className="terminal-body group h-[250px] md:h-[300px] flex flex-col">
                            <div className="scanline-effect" />
                            <div className="flex-grow">
                                {terminalHistory.map((line, i) => (
                                    <div key={i} className={`terminal-line mb-4 ${line.type === 'system' ? 'text-gray-500 italic' : line.type === 'user' ? 'text-secondary font-bold' : 'text-gray-200'}`}>
                                        {line.type === 'user' && <span className="terminal-prompt text-secondary mr-2">ramspheld/gpt</span>}
                                        {line.type === 'bot' && <span className="terminal-prompt text-primary">&gt;</span>}
                                        <div className="inline-block align-top">
                                            {Array.isArray(line.content) ? (
                                                <ul className="space-y-1 list-none p-0 m-0">
                                                    {line.content.map((item, j) => (
                                                        <li key={j} className={j === 0 && line.content.length > 1 ? "text-primary font-black tracking-widest uppercase mb-2 border-b border-primary/20 pb-1 flex items-center gap-2" : "text-gray-300 flex items-start gap-2"}>
                                                            {j > 0 && line.content.length > 1 && <span className="opacity-40 text-[10px] mt-1.5 font-mono">0{j}</span>}
                                                            <span className="flex-grow">{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <span>{line.content}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {isBotTyping && (
                                    <div className="terminal-line mb-4 flex items-center gap-2">
                                        <span className="terminal-prompt text-primary">&gt;</span>
                                        <div className="flex gap-1.5 items-center px-1">
                                            <motion.div
                                                animate={{ opacity: [0.3, 1, 0.3] }}
                                                transition={{ duration: 1, repeat: Infinity }}
                                                className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_#38bdf8]"
                                            />
                                            <motion.div
                                                animate={{ opacity: [0.3, 1, 0.3] }}
                                                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                                                className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_#38bdf8]"
                                            />
                                            <motion.div
                                                animate={{ opacity: [0.3, 1, 0.3] }}
                                                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                                                className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_#38bdf8]"
                                            />
                                        </div>
                                        <span className="text-[10px] font-mono text-primary/60 italic lowercase tracking-wider">processing query...</span>
                                    </div>
                                )}

                                <form onSubmit={handleTerminalSubmit} className="flex items-center gap-2 mt-2">
                                    <span className="terminal-prompt font-mono text-sm text-secondary animate-pulse">ramspheld/gpt</span>
                                    <input
                                        type="text"
                                        value={terminalInput}
                                        onChange={(e) => setTerminalInput(e.target.value)}
                                        placeholder="Type command..."
                                        className="bg-transparent border-none outline-none text-white font-mono text-sm flex-grow placeholder-gray-700 focus:ring-0 p-0 m-0 w-full"
                                    />
                                </form>
                                <div ref={terminalEndRef} />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>

            {/* About Section */}
            <section id="about" className="section-padding relative">
                <div className="max-w-6xl mx-auto">
                    <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-24">
                        <h2 className="text-4xl md:text-6xl font-bold mb-6">My <span className="text-gradient underline decoration-primary/30">Vision</span></h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">I bridge the gap between advanced AI research and practical, sustainable implementation in emerging markets.</p>
                    </motion.div>
                    <div className="grid md:grid-cols-3 gap-10">
                        {coreValues.map((value, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="glass-card p-10 rounded-3xl text-center group hover:border-primary/50 transition-colors">
                                <div className="w-24 h-24 bg-primary/10 rounded-2xl flex items-center justify-center text-4xl text-primary mx-auto mb-8 group-hover:scale-110 group-hover:rotate-6 transition-transform">{value.icon}</div>
                                <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                                <p className="text-gray-400 leading-relaxed">{value.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Experience Section */}
            <section id="experience" className="section-padding relative">
                <div className="max-w-4xl mx-auto px-6">
                    <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">Professional <span className="text-gradient">Experience</span></h2>
                    </motion.div>
                    <div className="relative border-l-2 border-primary/30 ml-3 md:ml-6 space-y-12">
                        {experiences.map((exp, i) => (
                            <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative pl-8 md:pl-12">
                                <div className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-primary shadow-[0_0_10px_rgba(14,165,233,0.5)]" />
                                <div className="glass-card p-6 md:p-8 rounded-2xl hover:border-primary/40 transition-all group">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                                        <div>
                                            <h3 className="text-xl md:text-2xl font-bold text-white group-hover:text-primary transition-colors">{exp.role}</h3>
                                            <p className="text-primary font-medium flex items-center gap-2">{exp.company} <span className="w-1 h-1 rounded-full bg-gray-400" /> {exp.type}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-bold border border-primary/20">{exp.period}</span>
                                            <p className="text-gray-500 text-sm mt-1">{exp.location}</p>
                                        </div>
                                    </div>
                                    <ul className="space-y-2">
                                        {exp.achievements.map((achievement, j) => (
                                            <li key={j} className="text-gray-400 text-sm md:text-base flex items-start gap-2">
                                                <span className="text-primary mt-1.5">▹</span>{achievement}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Projects Section */}
            <section id="projects" className="section-padding relative overflow-hidden">
                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
                        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                            <h2 className="text-4xl md:text-6xl font-bold mb-4">Projects & <span className="text-primary">Contributions</span></h2>
                            <p className="text-gray-400 text-xl">Transforming ideas into resilient digital ecosystems.</p>
                        </motion.div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-10">
                        {projects.map((project, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card overflow-hidden rounded-[2.5rem] flex flex-col group border-white/5 hover:border-primary/30 transition-all duration-500">
                                <div className="p-10 flex-grow">
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <h3 className="text-3xl font-bold mb-2 group-hover:text-primary transition-colors">{project.title}</h3>
                                            <p className="text-primary/70 font-semibold tracking-wide uppercase text-sm">{project.role}</p>
                                        </div>
                                        <div className="flex gap-4">
                                            <a href={project.github} target="_blank" rel="noopener noreferrer" className="w-10 h-10 glass-bubble hover:text-primary transition-all"><FaGithub /></a>
                                            <a href={project.link} target="_blank" rel="noopener noreferrer" className="w-10 h-10 glass-bubble hover:text-primary transition-all"><FaExternalLinkAlt /></a>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 mb-8 text-lg leading-relaxed">{project.desc}</p>
                                    <div className="flex flex-wrap gap-3">
                                        {project.stack.map(s => (
                                            <span key={s} className="px-4 py-1.5 bg-primary/5 rounded-full text-xs font-semibold border border-primary/10 text-primary/80">{s}</span>
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blogs Section */}
            <section id="blogs" className="section-padding bg-white/[0.02]">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                            <h2 className="text-4xl md:text-6xl font-bold mb-4">Strategic <span className="text-primary italic">Insights</span></h2>
                            <p className="text-gray-400 text-xl max-w-2xl">Thoughts on AI governance, software architecture, and the digital future of Africa.</p>
                        </motion.div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {blogs.map((blog, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="glass-card group p-8 rounded-[2.5rem] flex flex-col hover:border-primary/40 transition-all duration-500">
                                <span className="px-4 py-1.5 bg-primary/10 rounded-full text-[10px] font-black tracking-widest uppercase text-primary border border-primary/20 w-max mb-6">{blog.tag}</span>
                                <h3 className="text-2xl font-bold mb-4 group-hover:text-primary transition-colors leading-tight">{blog.title}</h3>
                                <p className="text-gray-400 mb-8 flex-grow line-clamp-3 leading-relaxed">{blog.excerpt}</p>

                                <div className="flex items-center justify-between pt-6 border-t border-white/5">
                                    <div className="flex items-center gap-3">
                                        <button onClick={() => shareBlog('twitter', blog.title, blog.link)} className="p-2 hover:text-primary transition-colors text-gray-500" title="Share on X"><FaXTwitter /></button>
                                        <button onClick={() => shareBlog('linkedin', blog.title, blog.link)} className="p-2 hover:text-primary transition-colors text-gray-500" title="Share on LinkedIn"><FaLinkedin /></button>
                                        <button onClick={() => shareBlog('whatsapp', blog.title, blog.link)} className="p-2 hover:text-primary transition-colors text-gray-500" title="Share on WhatsApp"><FaWhatsapp /></button>
                                    </div>
                                    <a href={blog.link} className="text-white hover:text-primary font-bold text-sm flex items-center gap-2 group/btn">
                                        Read <FaExternalLinkAlt className="text-xs group-hover/btn:translate-x-1 transition-transform" />
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Skills Section */}
            <section id="skills" className="section-padding">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl md:text-6xl font-bold mb-24 text-center">Neural <span className="text-primary">Capabilities</span></h2>
                    <div className="grid lg:grid-cols-3 gap-8">
                        {skills.map((skill, i) => (
                            <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="glass-card p-12 rounded-[3.5rem] relative group overflow-hidden">
                                <div className="absolute top-0 right-0 p-8 text-6xl text-white/5 group-hover:text-primary/20 transition-colors">{skill.icon}</div>
                                <h3 className="text-3xl font-bold mb-10 group-hover:text-primary transition-colors">{skill.name}</h3>
                                <div className="flex flex-wrap gap-3">
                                    {skill.list.map(s => (
                                        <motion.span key={s} whileHover={{ scale: 1.05, backgroundColor: 'rgba(14, 165, 233, 0.15)', borderColor: 'rgba(14, 165, 233, 0.3)' }} className="px-5 py-2.5 bg-white/5 rounded-2xl border border-white/10 text-sm font-medium cursor-default transition-all">{s}</motion.span>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Connect Section */}
            <section id="connect" className="section-padding pb-32">
                <div className="max-w-6xl mx-auto relative px-6 md:px-20">
                    <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
                        <span className="text-primary font-mono tracking-widest uppercase text-sm mb-4 block">Connections</span>
                        <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight text-white">Get in <span className="text-primary italic">Touch</span></h2>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">Ready to bring your vision to life? Fill out the form below and let's discuss how we can collaborate.</p>
                    </motion.div>

                    <div className="relative max-w-3xl mx-auto">
                        <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 md:p-12 shadow-2xl relative z-10">
                            <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-300 ml-1">First name</label>
                                        <input type="text" placeholder="Enter your first name" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all hover:bg-white/10" />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-300 ml-1">Last name</label>
                                        <input type="text" placeholder="Enter your last name" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all hover:bg-white/10" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-300 ml-1">Work email</label>
                                    <div className="relative">
                                        <FaEnvelope className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-500" />
                                        <input type="email" placeholder="ramspheldonyango@gmail.com" className="w-full bg-white/5 border border-white/10 rounded-2xl pl-14 pr-6 py-4 text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all hover:bg-white/10" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-gray-300 ml-1">Inquiry Type</label>
                                    <select className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white appearance-none focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all hover:bg-white/10 cursor-pointer">
                                        <option className="bg-slate-900">Project Collaboration</option>
                                        <option className="bg-slate-900">Hiring / Full-time</option>
                                        <option className="bg-slate-900">Consultation</option>
                                        <option className="bg-slate-900">Mentorship</option>
                                        <option className="bg-slate-900">Other</option>
                                    </select>
                                </div>
                                <button className="w-full bg-primary text-black font-black py-5 rounded-2xl text-lg hover:scale-[1.02] transition-transform shadow-[0_0_30px_rgba(14,165,233,0.4)] flex items-center justify-center gap-3">
                                    Submit Request <FaRocket />
                                </button>
                                <p className="text-center text-gray-500 text-xs mt-4">By contacting, you agree to the <span className="underline decoration-primary/30 cursor-pointer hover:text-primary">Terms of Service</span> and <span className="underline decoration-primary/30 cursor-pointer hover:text-primary">Privacy Policy</span>.</p>
                            </form>
                        </div>

                        <div className="hidden lg:flex flex-col gap-10 absolute -left-24 top-1/2 -translate-y-1/2 items-center opacity-40 hover:opacity-100 transition-all">
                            <div className="w-px h-24 bg-gradient-to-t from-primary/50 to-transparent" />
                            {socials.map((social) => (
                                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-2xl text-gray-400 hover:text-primary transition-all hover:scale-125 hover:translate-x-1" title={social.name}>{social.icon}</a>
                            ))}
                            <div className="w-px h-24 bg-gradient-to-b from-primary/50 to-transparent" />
                        </div>

                        <div className="hidden lg:flex flex-col gap-10 absolute -right-24 top-1/2 -translate-y-1/2 items-center opacity-40 hover:opacity-100 transition-all">
                            <div className="w-px h-24 bg-gradient-to-t from-primary/50 to-transparent" />
                            {socials.map((social) => (
                                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-2xl text-gray-400 hover:text-primary transition-all hover:scale-125 hover:-translate-x-1" title={social.name}>{social.icon}</a>
                            ))}
                            <div className="w-px h-24 bg-gradient-to-b from-primary/50 to-transparent" />
                        </div>

                        {/* Mobile Socials */}
                        <div className="flex flex-wrap gap-8 items-center justify-center mt-12 lg:hidden opacity-60">
                            {socials.map((social) => (
                                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-2xl text-gray-400 hover:text-primary transition-all">{social.icon}</a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <footer className="relative pt-24 pb-12 border-t border-white/5 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

                <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
                    {/* Brand Column */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <img src={logo} alt="Logo" className="h-10 w-auto" />
                            <span className="text-lg font-black tracking-tighter">RAMSPHELD<span className="text-primary">.DEV</span></span>
                        </div>
                        <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
                            Architecting resilient digital ecosystems and ethical AI solutions across Africa and beyond.
                        </p>
                        <div className="flex gap-4">
                            {socials.map((social) => (
                                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-primary transition-colors text-xl">
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-xs">Navigate</h4>
                        <ul className="space-y-4">
                            {['home', 'about', 'projects', 'blogs', 'skills'].map((id) => (
                                <li key={id}>
                                    <button onClick={() => scrollTo(id)} className="text-gray-500 hover:text-primary transition-colors text-sm capitalize">
                                        {id}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources */}
                    <div>
                        <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-xs">Insights</h4>
                        <ul className="space-y-4">
                            {blogs.map((blog, i) => (
                                <li key={i}>
                                    <a href={blog.link} className="text-gray-500 hover:text-primary transition-colors text-sm line-clamp-1">
                                        {blog.title}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Detail */}
                    <div>
                        <h4 className="text-white font-bold mb-8 uppercase tracking-widest text-xs">Handshake</h4>
                        <ul className="space-y-4 text-sm text-gray-500">
                            <li className="flex items-center gap-3">
                                <span className="text-primary">E:</span> ramspheldonyango@gmail.com
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="text-primary">M:</span> +254 743 864 874
                            </li>
                            <li className="flex items-center gap-3">
                                <span className="text-primary">L:</span> Nairobi, Kenya (GMT+3)
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-6 pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
                    <p className="text-gray-600 font-medium tracking-widest uppercase text-[10px]">
                        © {new Date().getFullYear()} Ramspheld Onyango Ochieng • Handcrafted in Kenya • Powered by AI
                    </p>
                    <div className="flex gap-8 text-[10px] items-center text-gray-600 font-bold uppercase tracking-[0.2em]">
                        <button className="hover:text-primary transition-colors">Documentation</button>
                        <button className="hover:text-primary transition-colors">Privacy Policy</button>
                        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="px-4 py-2 border border-white/10 rounded-full hover:border-primary/50 hover:text-primary transition-all">Back to Top ↑</button>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
